import L from "leaflet";
import {
  MAPA_CAPA_LOTES,
  fx_urlMapaRegistroFinca,
  ticketMapaRegistro,
} from "./MapaRegistroFinca";
import { SelectiveWms } from "./SelectiveWms";

/** Datos del filtro de una capa, como parámetros sueltos del GetMap (hoy solo
 *  `lotes`). La Api los valida y arma el filtro con el registro del ticket. */
type FiltroMapa = Record<string, string>;

/** Capa WMS con los LOTES del paso 4, y los dos resaltes que puede llevar uno.
 *
 *  **Es el único renderizador de un lote.** Antes el paso 4 dibujaba un
 *  `L.geoJSON` por lote, con su color de `LOTE_COLORS`, su tooltip permanente y
 *  sus listeners de click y hover. Eso traía tres costos que crecían con la
 *  cantidad de lotes: un `<path>` y un `<div>` de tooltip por lote que Leaflet
 *  reposiciona en cada movimiento del mapa, y un repintado completo
 *  (`clearLayers()` + rehacer todas las capas) por cada cambio de selección.
 *  Acá el mapa pide tiles y nada más.
 *
 *  **El nombre del lote lo escribe GeoServer**, desde la columna `nombre` de
 *  `tbl_temp_lotes_finca` — la misma que escribe el input del sidebar por
 *  `ActualizarLotes`. Un solo origen para el texto: cuando el guardado vuelve se
 *  repiden los tiles y la etiqueta ya dice lo nuevo (ver `refrescar`).
 *
 *  **Las etiquetas van en su PROPIA capa** (`_etiquetas`, con el estilo
 *  `sextante_lote_temporal_etiqueta`), que es la misma tabla pedida por segunda
 *  vez con un estilo que sólo dibuja texto. Son dos capas y no una por dos
 *  motivos:
 *
 *   - texto y geometría en el mismo estilo salen en el MISMO raster, y mientras
 *     un tile nuevo viaja el navegador reescala el que tiene: el texto se
 *     estiraba junto con el polígono y se veía como si la geometría se estuviera
 *     deformando;
 *   - cada una se repide con su propia cadencia — un renombre no mueve ningún
 *     borde, y un corte no cambia ninguna letra.
 *
 *  **Las cuatro capas van en grilla de tiles.** Hubo un intento de pedir los
 *  lotes como UNA imagen por vista para que las etiquetas no se repitieran: lo
 *  resolvía, pero una imagen única se congela y se estira en cada zoom hasta que
 *  llega la siguiente, que es peor que el problema. Que haya una etiqueta por
 *  lote y no una por tile lo resuelve el SLD anclándola al centroide (ver
 *  `sextante_lote_temporal_etiqueta.sld`), no el modo de petición.
 *
 *  **Una sola capa publicada para toda la plataforma**, igual que en el paso 3:
 *  el registro lo pone la Api en cada petición, a partir del ticket del mapa
 *  (ver `MapaRegistroFinca`), no creando una capa por finca.
 *
 *  **El resalte va por `id` de lote, que es un uuid real.** Es la diferencia con
 *  el resalte del paso 3, que tiene que nombrar la pieza por su posición en un
 *  `MultiPolygon` (`indice`) y por lo tanto deja de valer en cuanto se borra
 *  otra pieza. Acá el id sobrevive a todo lo que no sea borrar ese lote.
 */
export class LotesWmsLayer {
  /** Pane propio, ENTRE los tiles del satelital y los vectores (la línea de
   *  corte, los handles de vértice). */
  static readonly PANE = "lotesWmsPane";
  private static readonly PANE_Z_INDEX = 360;

  /** Pane de los VÉRTICES, por encima de los lotes y de sus etiquetas: un punto
   *  de 11 px que hay que poder agarrar no puede quedar debajo del relleno del
   *  lote vecino ni de un nombre. */
  static readonly PANE_VERTICES = "lotesVerticesWmsPane";
  private static readonly PANE_VERTICES_Z_INDEX = 370;

  /** EL resalte del paso 4: azul aguamarina `#40E0D0`, borde de 3 px y sin
   *  relleno. **Uno solo para los tres modos** — el lote elegido con un click
   *  (o resaltado por el foco del input de nombre), el que espera la
   *  confirmación de Borrar y los dos que esperan la de Unir.
   *
   *  Es el mismo resalte del paso 3 (`sextante_poligono_padre_eliminar`) y el
   *  mismo aguamarina del contorno del lote a dividir (`CONTORNO_A_DIVIDIR`):
   *  los dos pasos señalan igual porque dicen lo mismo.
   *
   *  Antes eran DOS estilos y los dos amarillos: borde `#FFFF00` para el
   *  seleccionado y relleno `#FFFF00` al 35% para el que esperaba
   *  confirmación. Dos amarillos distintos para el mismo concepto, y ninguno
   *  parecido al paso 3. El relleno además se sumaba al del estilo de reposo
   *  —esto va en una capa aparte, encima— así que el lote resaltado se leía
   *  como "más opaco" en vez de como "marcado". */
  private static readonly ESTILO_RESALTE =
    "sextante_lote_temporal_seleccionado";
  /** Sólo el nombre del lote, sin geometría, anclado a su centroide. */
  private static readonly ESTILO_ETIQUETA = "sextante_lote_temporal_etiqueta";
  /** Los vértices del lote (del `vertices()` sobre su geometría), para el modo
   *  Editar: blancos con borde naranja, como los del paso 3 en reposo. */
  private static readonly ESTILO_VERTICE = "sextante_lote_temporal_vertice";

  /** Margen de renderizado, en píxeles, que se le pide a GeoServer para la capa
   *  de etiquetas.
   *
   *  **Es lo que evita que una palabra salga cortada en la costura de dos
   *  tiles.** La etiqueta está anclada al centroide del lote; si ese punto cae
   *  cerca del borde de un tile, el texto se sale de su lienzo y el tile vecino
   *  NO lo dibuja — GeoServer recorta la feature al área del tile, así que el
   *  punto de la etiqueta queda fuera y no hay etiqueta que dibujar. El
   *  resultado es media palabra.
   *
   *  Con `buffer`, cada tile renderiza también lo que cae hasta esa distancia
   *  fuera de sus bordes: el vecino dibuja la mitad que le toca y, al unirse los
   *  dos tiles, la palabra se lee entera.
   *
   *  256 px cubre de sobra el ancho máximo de una etiqueta (`autoWrap` la parte
   *  antes). Sólo se le pide a la capa de etiquetas: la de polígonos no lo
   *  necesita —un borde de 3 px no se corta de forma visible— y pedirlo de más
   *  hace que GeoServer consulte y dibuje geometrías que después descarta. */
  private static readonly BUFFER_ETIQUETAS_PX = 256;

  constructor(
    private readonly _map: L.Map,
    private readonly _registroId: string,
  ) {}

  /** Crea el pane y engancha las capas. Sin `registroId` no hace nada: no hay
   *  registro del que pedir ticket. */
  addTo(): void {
    if (!this._registroId || this._lotes) return;

    for (const [nombre, zIndex] of [
      [LotesWmsLayer.PANE, LotesWmsLayer.PANE_Z_INDEX],
      [LotesWmsLayer.PANE_VERTICES, LotesWmsLayer.PANE_VERTICES_Z_INDEX],
    ] as const) {
      if (this._map.getPane(nombre)) continue;
      const pane = this._map.createPane(nombre);
      pane.style.zIndex = String(zIndex);
      // Los clicks tienen que seguir llegando al mapa: el hit-testing de qué
      // lote se tocó lo resuelve `LoteSplitState.enElPunto` contra el modelo, y
      // la línea de corte necesita el click para poner su vértice. Un raster no
      // se puede clickear por feature de todas formas.
      pane.style.pointerEvents = "none";
    }

    this._lotes = this._crearCapa(this._filtroRegistro());
    // Los resaltes van DESPUÉS: dentro del pane, el orden de alta decide quién
    // tapa a quién, y el resalte tiene que quedar encima del naranja del mismo
    // lote. Nacen sin lote elegido, o sea sin pedir ningún tile.
    this._seleccionado = this._crearCapa(this._filtroNinguno());
    this._elegidos = this._crearCapa(this._filtroNinguno());
    // Las etiquetas, ARRIBA de todo: el nombre de un lote no lo tiene que tapar
    // ni el relleno del vecino ni un resalte.
    this._etiquetas = this._crearCapa(
      this._filtroRegistro(),
      LotesWmsLayer.ESTILO_ETIQUETA,
      LotesWmsLayer.BUFFER_ETIQUETAS_PX,
    );
    // Los vértices nacen apagados: los prende `mostrarVertices` cuando el modo
    // Editar engancha un lote.
    this._vertices = this._crearCapa(
      this._filtroNinguno(),
      LotesWmsLayer.ESTILO_VERTICE,
      null,
      LotesWmsLayer.PANE_VERTICES,
    );

    // Hasta que llega el ticket del mapa ninguna capa pide tiles (ver
    // `SelectiveWms._isValidTile`); cuando llega, se repintan.
    void ticketMapaRegistro.asegurarAsync(this._registroId).then(() => {
      for (const capa of [
        this._lotes,
        this._seleccionado,
        this._elegidos,
        this._etiquetas,
        this._vertices,
      ]) {
        capa?.redraw();
      }
    });
  }

  /** Muestra los vértices de UN lote: el del borde que está bajo el cursor en
   *  modo Editar.
   *
   *  Son la contraparte en reposo de los handles que pone
   *  `LoteVertexEditEngine`: estos se ven todos y son blancos; el que el cursor
   *  engancha se dibuja encima, verde y más grande. */
  mostrarVertices(
    loteId: string | null,
    bbox: [number, number, number, number] | null = null,
  ): void {
    this._aplicarResalte(
      this._vertices,
      loteId ? [loteId] : [],
      LotesWmsLayer.ESTILO_VERTICE,
      bbox,
    );
  }

  /** Repinta los lotes.
   *
   *  Lo llama el paso 4 cuando el servidor confirmó un cambio —un corte, una
   *  unión, un borrado, un nombre nuevo—, porque es recién ahí que la base
   *  tiene lo que esta capa dibuja. Pedirlo antes traería el estado anterior.
   *
   *  Con `bounds` se repiden SÓLO los tiles de esa zona; sin él, todos. Un
   *  nombre que cambia no mueve ninguna geometría, pero sí cambia el texto, y
   *  el texto puede caer en cualquier tile que toque el lote.
   *
   *  **Las etiquetas se repiden en una zona más ancha** (2026-10-27, desde que
   *  el paso 4 repinta por zona, ver `LoteSplitMapRender.refrescarLotes`): el
   *  nombre va anclado al centroide del lote y puede salirse de su caja —en un
   *  lote angosto el texto es más ancho que el lote—, y GeoServer lo dibuja
   *  también en los tiles vecinos hasta `BUFFER_ETIQUETAS_PX`. Si esos tiles no
   *  se repiden, queda en ellos un pedazo del nombre viejo. */
  refrescar(bounds: L.LatLngBounds | null = null): void {
    for (const capa of [
      this._lotes,
      this._seleccionado,
      this._elegidos,
      this._etiquetas,
      this._vertices,
    ]) {
      if (!capa) continue;
      if (!bounds) capa.refrescarTodo();
      else if (capa === this._etiquetas) {
        capa.refrescarBounds(
          this._ampliarPx(bounds, LotesWmsLayer.BUFFER_ETIQUETAS_PX),
        );
      } else capa.refrescarBounds(bounds);
    }
  }

  /** La zona agrandada `px` píxeles hacia cada lado, medidos en el zoom actual
   *  (misma cuenta que el margen de `SelectiveWms.refrescarBounds`). */
  private _ampliarPx(bounds: L.LatLngBounds, px: number): L.LatLngBounds {
    const zoom = this._map.getZoom();
    const sw = this._map.project(bounds.getSouthWest(), zoom);
    const ne = this._map.project(bounds.getNorthEast(), zoom);
    const p1 = L.point(Math.min(sw.x, ne.x) - px, Math.min(sw.y, ne.y) - px);
    const p2 = L.point(Math.max(sw.x, ne.x) + px, Math.max(sw.y, ne.y) + px);
    return L.latLngBounds(
      this._map.unproject(p1, zoom),
      this._map.unproject(p2, zoom),
    );
  }

  /** Resalta UN lote con el borde aguamarina del paso: es el que el
   *  usuario eligió con un click, o el que resalta el foco del input de nombre.
   *
   *  `null` saca el resalte. El `bbox` acota los tiles a pedir; sin él, Leaflet
   *  pediría los del viewport entero. */
  seleccionar(
    loteId: string | null,
    bbox: [number, number, number, number] | null = null,
  ): void {
    this._aplicarResalte(
      this._seleccionado,
      loteId ? [loteId] : [],
      LotesWmsLayer.ESTILO_RESALTE,
      bbox,
    );
  }

  /** Resalta los lotes que esperan la confirmación de Unir o de
   *  Borrar. Un arreglo vacío saca el resalte. */
  elegir(
    loteIds: readonly string[],
    bbox: [number, number, number, number] | null = null,
  ): void {
    this._aplicarResalte(
      this._elegidos,
      loteIds,
      LotesWmsLayer.ESTILO_RESALTE,
      bbox,
    );
  }

  /** Espera a que el resalte de `elegir` esté EN PANTALLA.
   *
   *  El color lo pinta GeoServer, así que entre el click y el resalte hay una
   *  ida y vuelta. Quien pide un borrado abre encima un modal de confirmación,
   *  y sin esta espera el modal salía primero y el resalte aparecía después,
   *  debajo: había que confirmar sin ver qué lote se iba a borrar. Mismo
   *  arreglo que en el paso 3 (`AoiWmsLayer.resaltarParaBorrar`).
   *
   *  Se llama DESPUÉS de `elegir`, no antes: lo que se espera es el `load` de
   *  los tiles que esa llamada acaba de pedir.
   *
   *  Resuelve igual a los `ESPERA_RESALTE_MS` si GeoServer no contesta, o si no
   *  hubo tiles nuevos que pedir y `load` no llega a dispararse: más vale un
   *  resalte tarde que un modal que nunca abre. */
  esperarResalteAsync(): Promise<void> {
    const capa = this._elegidos;
    if (!capa) return Promise.resolve();

    return new Promise<void>((resolve) => {
      let resuelto = false;
      const terminar = (pausa: number): void => {
        if (resuelto) return;
        resuelto = true;
        capa.off("load", alCargar);
        clearTimeout(reloj);
        if (pausa > 0) setTimeout(resolve, pausa);
        else resolve();
      };

      // El resalte ya está pintado: se deja un respiro antes de que el modal
      // lo tape (ver `PAUSA_ANTES_DEL_MODAL_MS`).
      const alCargar = (): void =>
        terminar(LotesWmsLayer.PAUSA_ANTES_DEL_MODAL_MS);

      // Por el tope no se suma pausa: ya se esperó de más.
      const reloj = setTimeout(
        () => terminar(0),
        LotesWmsLayer.ESPERA_RESALTE_MS,
      );
      capa.on("load", alCargar);
    });
  }

  /** Cuánto se espera, como mucho, a que el resalte aparezca. Con GeoServer
   *  respondiendo en ~25 ms por tile nunca se llega. */
  private static readonly ESPERA_RESALTE_MS = 800;

  /** Pausa entre que el resalte aparece y que se abre el modal. GeoServer
   *  contesta en pocas decenas de ms, así que sin esto el modal tapa el color
   *  casi en el mismo cuadro en que se pintó: el usuario no llega a registrar
   *  QUÉ lote eligió. Un quinto de segundo alcanza para verlo y no se siente
   *  como una demora.
   *
   *  No se aplica cuando se resolvió por el tope de espera: ahí ya pasaron
   *  800 ms y lo que falta es abrir el modal, no esperar más. */
  private static readonly PAUSA_ANTES_DEL_MODAL_MS = 200;


  /** Amplía la extensión conocida de los datos.
   *
   *  **Esto es lo que evita las peticiones al vacío.** Leaflet NO mira el
   *  bounding box que la capa declara en GetCapabilities: `L.TileLayer` pide
   *  los tiles de todo el viewport. Lo único que lo limita es `options.bounds`
   *  de `L.GridLayer`, y hay que ponerla a mano. */
  extender(bounds: L.LatLngBounds): void {
    if (!this._lotes) return;
    const anterior = this._boundsLotes;
    this._boundsLotes = anterior
      ? L.latLngBounds([
          anterior.getSouthWest(),
          anterior.getNorthEast(),
        ]).extend(bounds)
      : bounds;
    if (anterior && anterior.equals(this._boundsLotes)) return;

    // Las dos capas de datos comparten extensión: son la misma tabla.
    for (const capa of [this._lotes, this._etiquetas]) {
      if (!capa) continue;
      (capa.options as L.GridLayerOptions).bounds = this._boundsLotes;
      // Al ampliarse hay tiles nuevos que antes se descartaban, y esos Leaflet
      // sólo los evalúa en su `_update` o tras un `redraw`.
      //
      // `refrescarTodo` y no `redraw` a secas: la extensión se amplía cuando
      // los lotes cambiaron, y `redraw` repide los tiles con la MISMA url —no
      // sube `_v`—, así que lo único que evitaría una tesela cacheada sería el
      // `no-store` de la Api. Lo que se está dibujando no puede depender de un
      // header para verse al día.
      capa.refrescarTodo();
    }
  }

  dispose(): void {
    for (const capa of [
      this._lotes,
      this._seleccionado,
      this._elegidos,
      this._etiquetas,
      this._vertices,
    ]) {
      if (capa) this._map.removeLayer(capa);
    }
    this._lotes = null;
    this._seleccionado = null;
    this._elegidos = null;
    this._etiquetas = null;
    this._vertices = null;
  }

  // ------------------------------------------------------------------ interno

  /** Todos los lotes vivos del registro: la Api ya filtra por registro y por
   *  `eliminado=false`, así que no hace falta mandar nada. */
  private _filtroRegistro(): FiltroMapa {
    return {};
  }

  /** Filtro que no puede resolver a nada, para una capa de resalte sin lote.
   *
   *  Un uuid de ceros no es ningún `id` real: los genera `Guid.CreateVersion7()`
   *  del servidor, que nunca da el nulo. Es el mismo recurso que usa la capa de
   *  vértices del paso 3, y por el mismo motivo: un filtro "vacío" que en
   *  realidad resuelve a algo deja la capa a un `redraw()` ajeno de mostrar lo
   *  que no debe. */
  private _filtroNinguno(): FiltroMapa {
    return { lotes: "00000000-0000-0000-0000-000000000000" };
  }

  private _aplicarResalte(
    capa: SelectiveWms | null,
    loteIds: readonly string[],
    estilo: string,
    bbox: [number, number, number, number] | null,
  ): void {
    if (!capa) return;

    const filtro: FiltroMapa = loteIds.length
      ? { lotes: loteIds.join(",") }
      : this._filtroNinguno();

    capa.setParams(
      {
        ...filtro,
        styles: estilo,
      } as unknown as L.WMSParams,
      true,
    );
    // Sin lote, o sin su caja, la extensión queda degenerada y la capa no pide
    // ningún tile.
    (capa.options as L.GridLayerOptions).bounds =
      loteIds.length && bbox
        ? L.latLngBounds(L.latLng(bbox[1], bbox[0]), L.latLng(bbox[3], bbox[2]))
        : L.latLngBounds([0, 0], [0, 0]);
    capa.refrescarTodo();
  }

  /** Arma una capa WMS sobre la tabla de lotes, con su filtro y, si hace falta,
   *  su estilo.
   *
   *  Sin `estilo` la capa usa el que tiene por defecto en GeoServer
   *  (`sextante_lote_temporal`: el polígono, sin texto). Lo pasan las etiquetas
   *  —que son la misma tabla con un estilo que sólo dibuja el nombre— y, en cada
   *  GetMap, los dos resaltes (ver `_aplicarResalte`). */
  private _crearCapa(
    filtro: FiltroMapa,
    estilo: string | null = null,
    bufferPx: number | null = null,
    pane: string = LotesWmsLayer.PANE,
  ): SelectiveWms {
    const wms = new SelectiveWms(
      fx_urlMapaRegistroFinca(),
      {
        layers: MAPA_CAPA_LOTES,
        // Sin workspace: la Api lo valida contra los estilos de la capa.
        ...(estilo ? { styles: estilo } : {}),
        // `buffer` no es de Leaflet: cualquier opción que no sea suya termina
        // en los parámetros del GetMap, que es justo lo que se quiere acá.
        ...(bufferPx ? { buffer: bufferPx } : {}),
        format: "image/png",
        transparent: true,
        version: "1.1.1",
        maxZoom: 20,
        pane,
        // Los datos del filtro como parámetros sueltos (`lotes`); el registro
        // lo pone la Api a partir del ticket.
        ...filtro,
        uppercase: true,
        ticketMapa: () => ticketMapaRegistro.actual(this._registroId),
        bounds: L.latLngBounds([0, 0], [0, 0]),
      } as L.WMSOptions & { ticketMapa: () => string },
    );
    wms.addTo(this._map);
    return wms;
  }

  private _boundsLotes: L.LatLngBounds | null = null;
  private _lotes: SelectiveWms | null = null;
  /** Sólo los nombres, sin geometría. */
  private _etiquetas: SelectiveWms | null = null;
  /** Los vértices del lote enganchado por el modo Editar, o nada. */
  private _vertices: SelectiveWms | null = null;
  private _seleccionado: SelectiveWms | null = null;
  private _elegidos: SelectiveWms | null = null;
}
